import {observableDeep} from "mobx";
import {Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";

export const objPlacementDefaults: ObjPlacementMap = {
};

/** A placement override for a particular model/object (or sub-object within it). Fields left undefined fall back to the defaults (in the code / .glb).
 * Note: Must be a plain object type (not a class), because mobx's deep-observation enhancer does not convert class instances — class instances stored
 * in the placements map would be silently non-observable (field-changes not triggering scene/dialog updates). */
export type ObjPlacement = {
	position?: [number, number, number],
	rotation?: [number, number, number], // euler angles, as used by three.js
	/** Single number is expanded to [x,y,z] = [x,x,x]. */
	scale?: number | [number, number, number],
};

/** The structural shape of a tracked object (avoiding a hard three.js import in this module). */
export type TrackedObject = {
	name: string,
	position: {set: (x: number, y: number, z: number)=>void, x: number, y: number, z: number},
	rotation: {set: (x: number, y: number, z: number)=>void, x: number, y: number, z: number},
	scale: {set: (x: number, y: number, z: number)=>void, x: number, y: number, z: number},
	updateMatrix?: ()=>void,
};

/** The placement-overrides map: key = "<filename-with-wildcard-version>/<sub-object-path>", with the "/" part optional (eg. for file-level overrides). */
export type ObjPlacementMap = Record<string, ObjPlacement>;

/** The placements store: holds the live overrides-map (seeded from the code-level defaults at app-open; editable via the placements-dialog in Settings->VR)
 * and the registry of objects loaded through useSimple3DComp (drives the placements-dialog tree-view).
 * Both maps are deeply mobx-observable, so subscribers (useSimple3DComp, the placements-dialog) react to any entry-add/delete/field-change. */
class ObjPlacementsStore {
	/** key = "<filename-with-wildcard-version>/<sub-object-path>" (or just the file-key for model-level overrides). */
	@observableDeep accessor map: ObjPlacementMap = {...objPlacementDefaults};
	/** key = file-key (eg. "SofaSet*.glb"), value = the model's path and its registered sub-objects (key = sub-object-path). */
	@observableDeep accessor models: Record<string, {path: string, objects: Record<string, TrackedObject>}> = {};
}
export const objPlacementsStore = new ObjPlacementsStore();

/** Resolves the placement-key for a model-path + sub-object-path (eg. "SofaSet*.glb/Chair_Left"). */
export function GetPlacementKey(path: string, subObjectPath: string) {
	return `${ModelPathToPlacementFileKey(path)}${subObjectPath ? `/${subObjectPath}` : ""}`;
}

export function RegisterPlacementObject(path: string, subObjectPath: string, obj: TrackedObject) {
	const map = objPlacementsStore.models;
	const fileKey = ModelPathToPlacementFileKey(path);
	map[fileKey] ??= {path, objects: {}};
	map[fileKey].objects[subObjectPath] = obj;
	// note: no placement-map entry gets pre-created here — the autorun in useSimple3DComp iterates the map's keys (so subscribes to key-adds),
	// meaning an entry created later (eg. when the user enables a field in the placements-dialog) triggers a re-run, which then subscribes to the new entry's fields.
	// (pre-created empty entries would also pollute the "Copy JSON" output with empty {} entries)
}
export function UnregisterPlacementObject(path: string, subObjectPath: string) {
	const map = objPlacementsStore.models;
	const fileKey = ModelPathToPlacementFileKey(path);
	const model = map[fileKey];
	if (model == null) return;
	delete model.objects[subObjectPath];
	// drop the map-entry if it's still empty (an override was never enabled); if it has overrides, keep it so they re-apply when the object returns
	const entry = objPlacementsStore.map[GetPlacementKey(path, subObjectPath)];
	if (entry != null && entry.position == null && entry.rotation == null && entry.scale == null) {
		delete objPlacementsStore.map[GetPlacementKey(path, subObjectPath)];
	}
	if (Object.keys(model.objects).length == 0) delete map[fileKey]; // drop the model-group once empty
}

/** Resolves a model-path (eg. "/3D/ZUp/SofaSet_3.1.glb") to the "normalized" file-key used in the placements map (eg. "SofaSet*.glb"). */
export function ModelPathToPlacementFileKey(path: string) {
	const fileName = path.split("/").pop() ?? ""; // eg. "SofaSet_3.1.glb"
	// replace version part with "*", eg. "SofaSet_3.1.glb" -> "SofaSet*.glb"
	const dotIndex = fileName.lastIndexOf(".");
	const beforeExt = dotIndex == -1 ? fileName : fileName.slice(0, dotIndex);
	const ext = dotIndex == -1 ? "" : fileName.slice(dotIndex);
	// find the last "_" section, and replace it (and anything after it within the pre-extension part) with "*"
	const underscoreIndex = beforeExt.lastIndexOf("_");
	const baseName = underscoreIndex == -1 ? beforeExt : beforeExt.slice(0, underscoreIndex);
	return `${baseName}*${ext}`;
}

/** Returns the placement-data for the given model-path and sub-object-path (the latter being optional), searching from most-specific to least.
 * Note: Reads the observable map, so calls made within a mobx tracking-scope (eg. autorun/observer) are reactive to map changes. */
export function FindPlacement(path: string, subObjectPath?: string): ObjPlacement {
	const map = objPlacementsStore.map;
	const fileKey = ModelPathToPlacementFileKey(path);
	if (subObjectPath != null) {
		return map[`${fileKey}/${subObjectPath}`] ?? map[fileKey] ?? {};
	}
	return map[fileKey] ?? {};
}

/** Applies a placement to an object (a THREE.Object3D, but we avoid importing three here to keep this module light for the store/UI). */
export function ApplyPlacement(obj: {position: {set: (x: number, y: number, z: number)=>void}, rotation: {set: (x: number, y: number, z: number)=>void}, scale: {set: (x: number, y: number, z: number)=>void}, updateMatrix?: ()=>void}, placement: ObjPlacement) {
	if (placement.position) obj.position.set(...placement.position);
	if (placement.rotation) obj.rotation.set(...placement.rotation);
	if (placement.scale != null) {
		const s = typeof placement.scale == "number" ? [placement.scale, placement.scale, placement.scale] as const : placement.scale;
		obj.scale.set(...s);
	}
	obj.updateMatrix?.();
}

/** Convenience for constructing a Vec3 from an optional placement position (falls back to a default when unset). Useful for the group-level position in useSimple3DComp. */
export function GetPlacementVec3(placement: ObjPlacement, defaultVal: Vec3) {
	return placement.position ? new Vec3(...placement.position) : defaultVal;
}
